/**
 * Shadow proxies: one merged, skinned, shadow-only mesh per character instead of one shadow
 * draw per body part (a VRoid hero has ~10 opaque casters, a KayKit mob 2).
 *
 * The proxy merges the positions + skin attributes of every opaque, non-morphing skinned caster
 * that shares one Skeleton, with each part's bind matrix baked into its vertices, and lives on
 * layer SHADOW_LAYER only: the key light's shadow camera sees it, the main camera doesn't. The
 * parts stop casting. The shadow is the same silhouette (cut-out / transparent parts keep
 * casting on their own), at one draw call.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Layer seen only by shadow cameras. */
export const SHADOW_LAYER = 3;

/** Proxy materials by cut-out texture + alphaTest + side (never drawn in the main pass). */
const proxyMats = new Map<string, THREE.MeshBasicMaterial>();
function proxyMaterial(src: THREE.Material & { map?: THREE.Texture | null }, keepCut: boolean): THREE.MeshBasicMaterial {
  const cut = keepCut && src.alphaTest > 0 && src.map ? src.map : null;
  const key = `${cut?.uuid ?? '-'}|${cut ? src.alphaTest : 0}|${src.side}`;
  let m = proxyMats.get(key);
  if (!m) {
    m = new THREE.MeshBasicMaterial({ colorWrite: false, map: cut, alphaTest: cut ? src.alphaTest : 0, side: src.side });
    m.name = 'shadowProxy';
    proxyMats.set(key, m);
  }
  return m;
}

/** Merged geometry per cache key (models cloned many times share one). */
const geoCache = new Map<string, THREE.BufferGeometry | null>();

function casters(root: THREE.Object3D): THREE.SkinnedMesh[] {
  const out: THREE.SkinnedMesh[] = [];
  root.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh || !m.castShadow || m.userData['hull'] || m.userData['shadowProxy']) return;
    if (Object.keys(m.geometry.morphAttributes).length > 0) return;
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    if (mats.length !== 1 || mats.some((x) => x.transparent || !x.visible)) return;
    out.push(m);
  });
  return out;
}

function mergeFor(parts: THREE.SkinnedMesh[], withUv: boolean): THREE.BufferGeometry | null {
  const geos: THREE.BufferGeometry[] = [];
  for (const p of parts) {
    const src = p.geometry;
    const pos = src.attributes['position'];
    const si = src.attributes['skinIndex'];
    const sw = src.attributes['skinWeight'];
    if (!pos || !si || !sw) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', pos.clone());
    const uv = src.attributes['uv'];
    if (withUv) {
      if (!uv) return null;
      g.setAttribute('uv', uv.clone());
    }
    g.setAttribute('skinIndex', new THREE.BufferAttribute(new Uint16Array(Array.from({ length: si.count * 4 }, (_, i) => si.getComponent(Math.floor(i / 4), i % 4))), 4));
    g.setAttribute('skinWeight', new THREE.BufferAttribute(new Float32Array(Array.from({ length: sw.count * 4 }, (_, i) => sw.getComponent(Math.floor(i / 4), i % 4))), 4));
    if (src.index) g.setIndex(src.index.clone());
    else g.setIndex(Array.from({ length: pos.count }, (_, i) => i));
    g.applyMatrix4(p.bindMatrix);
    geos.push(g);
  }
  const merged = mergeGeometries(geos, false);
  geos.forEach((g) => g.dispose());
  return merged;
}

/**
 * Group key: parts in one proxy share the skeleton and the shadow's cut-out state. Only hair
 * keeps its alpha cut-out (strand tips); the shadow-map texel (~3 cm) is coarser than any
 * cut-out detail on bodies and clothes, so those merge into one solid proxy.
 */
const CUTOUT_PARTS = /hair/i;
function groupKey(m: THREE.SkinnedMesh): string {
  const mat = m.material as THREE.Material & { map?: THREE.Texture | null };
  const keepCut = mat.alphaTest > 0 && !!mat.map && (CUTOUT_PARTS.test(m.name) || CUTOUT_PARTS.test(mat.name));
  const cut = keepCut ? `${mat.map!.uuid}@${mat.alphaTest}` : '-';
  return `${m.skeleton.uuid}|${cut}|${keepCut ? 'cut' : 'solid'}${mat.side}`;
}

/**
 * Give `root`'s skinned casters shadow proxies: one per group of parts that share the skeleton
 * and the cut-out texture (alpha-tested hair keeps its cut-out shadow). `key` caches merged
 * geometry for models that are cloned (same part order). Returns the proxies made.
 */
export function attachShadowProxy(root: THREE.Object3D, key?: string): THREE.SkinnedMesh[] {
  const groups = new Map<string, THREE.SkinnedMesh[]>();
  for (const p of casters(root)) {
    const k = groupKey(p);
    groups.set(k, [...(groups.get(k) ?? []), p]);
  }
  const out: THREE.SkinnedMesh[] = [];
  let gi = 0;
  for (const parts of groups.values()) {
    const idx = gi++;
    if (parts.length < 2) continue;
    const src = parts[0]!.material as THREE.Material & { map?: THREE.Texture | null };
    const mat = proxyMaterial(src, !groupKey(parts[0]!).includes('|solid'));
    const ck = key !== undefined ? `${key}#${idx}` : undefined;
    let geo = ck !== undefined ? geoCache.get(ck) : undefined;
    if (geo === undefined) {
      geo = mergeFor(parts, !!mat.map);
      if (ck !== undefined) geoCache.set(ck, geo);
    }
    if (!geo) continue;
    const proxy = new THREE.SkinnedMesh(geo, mat);
    proxy.name = 'shadowProxy';
    proxy.userData['shadowProxy'] = true;
    proxy.userData['noStyle'] = true;
    proxy.castShadow = true;
    proxy.receiveShadow = false;
    proxy.frustumCulled = false;
    proxy.layers.set(SHADOW_LAYER);
    parts[0]!.parent!.add(proxy);
    proxy.bind(parts[0]!.skeleton, new THREE.Matrix4());
    for (const p of parts) p.castShadow = false;
    out.push(proxy);
  }
  return out;
}
